import React, { useEffect } from "react"
import cn from "classnames";
import { useForm } from "react-final-form";
import AmountAndPeriodFields from "./AmountAndPeriodFields";
import Box from "../boxes/Box"
import Button from "../controls/Button";
import ContinueButton from "../controls/ContinueButton"
import DataProcessingInfo from "../texts/DataProcessingInfo";
import DescriptionBox from "../boxes/DescriptionBox"
import Header from "../boxes/Header"
import LoanPurposeField from "../form-fields/LoanPurposeField"
import { LoanType } from "../../types/loan";
import {
  calculateMonthlyPayment,
  calculateMaxLoanPeriod,
  clearLocalLoan,
  calculateMaxLoanAmount,
  calculateRemainingAmountAfterRefinance
} from "../../utils/loan";
import { formatCurrency } from "../../utils/formats"
import { t } from "../../i18n";
import { useNavigate } from "@reach/router";
import { useUserContext } from "../../context/user";
import LoaderModal from "../modals/LoaderModal";
import SwitchField from "../form-fields/SwitchField";

interface Props {
  amount: number
  loanType: LoanType
  minAmount?: null | number
  period: number
  previousLoanDebt?: null | number
  fullInternalRefinanceFee: number
  refinancingOnly?: boolean
  submitting?: boolean
  obligationsConsent?: boolean | null
}

const LoanFields = ({ amount, minAmount, period, refinancingOnly, submitting, loanType, previousLoanDebt, fullInternalRefinanceFee, obligationsConsent }: Props): JSX.Element => {
  const { user } = useUserContext();
  const navigate = useNavigate();

  const repaymentValue = calculateMonthlyPayment(amount, period)
  const maxLoanPeriod = calculateMaxLoanPeriod(amount, loanType);
  const maxLoanAmount = calculateMaxLoanAmount(window.location.search.includes("type=small"), refinancingOnly);
  const form = useForm();

  useEffect(() => {
    if (period <= maxLoanPeriod) return;
    form.change("period", maxLoanPeriod);
  }, [maxLoanPeriod]);

  const handleChangeLoanType = (type: LoanType) => {
    if (user) return;
    clearLocalLoan();
    void navigate(type === "personal" ? "new" : "new?type=business");
  };

  return (
    <>
      <div className="d-flex justify-between max-w-screen-minus-xs md:max-w-lg overflow-x-auto">
        <Button
          className={cn("text-curious-blue u-h-100 p-0 text-left", { "text-cod-gray": loanType === "personal" })}
          onClick={() => handleChangeLoanType("personal")}
          transparent
        >
          <Header
            level="2"
            i18nKey="loan.loan_types.personal_loans"
            className="text-4xl-bold md:whitespace-nowrap"
          />
        </Button>
        <Button
          className={cn("text-curious-blue u-h-100 p-0 text-left", {
            "hidden": loanType !== "business", // Remove it after created business loan type for back
            "text-cod-gray": loanType === "business",
          })}
          onClick={() => handleChangeLoanType("business")}
          transparent
        >
          <Header
            level="2"
            i18nKey="loan.loan_types.business_loans"
            className="text-4xl-bold md:whitespace-nowrap"
          />
        </Button>
      </div>
      {refinancingOnly ?
        <p className="mt-7">
          {t("loan.choose_amount_and_period_refinance")}
        </p> :
        <p className="mt-7">
          {t("loan.choose_amount_and_period")}
        </p>
      }
      {refinancingOnly && previousLoanDebt && (
        <Box className="mz-new-loan-desc" gap="40px" maxWidth="480px">
          <div className="p-4">
            <div className="text-4xl-bold">{formatCurrency(previousLoanDebt)}</div>
            <DescriptionBox
              i18nText=""
              i18nTitle="loan.previous_loan_debt_title"
              smallText
            />
          </div>
          <Box className="border-4 border-solid border-curious-blue p-3 bg-pattens-blue" maxWidth="210px" minWidth="210px">
            <div className="text-4xl-bold">{formatCurrency(calculateRemainingAmountAfterRefinance(previousLoanDebt, fullInternalRefinanceFee, amount))}</div>
            <DescriptionBox
              i18nText=""
              i18nTitle="loan.amount_to_be_credited"
              smallText
            />
            <p className="text-xs mt-1">{t("loan.amount_to_be_credited_note")}</p>
          </Box>
        </Box>
      )}
      <AmountAndPeriodFields
        maxAmount={maxLoanAmount}
        minAmount={minAmount || 500}
        amountStep={100}
        maxPeriod={maxLoanPeriod}
        minPeriod={3}
        periodStep={1}
      />
      <div className="max-w-22r flex items-center gap-4 mt-8">
        <Box className="text-4xl-bold" maxWidth="160px" minWidth="160px">{formatCurrency(repaymentValue)}</Box>
        <DescriptionBox
          i18nText="loan.repayment_desc"
          i18nTitle="loan.repayment"
          smallText
        />
      </div>
      {!refinancingOnly && (
        <Box gap="40px">
          <LoanPurposeField
            i18nKey="loan.purpose"
            loanType={loanType}
            name="purpose"
            refinancingOnly={refinancingOnly}
          />
        </Box>
      )}
      <Box>
        {refinancingOnly && (
          <SwitchField
            className="u-mt-md"
            i18nKey="loan.agreed_to_cover_obligations"
            id="switch_agreed_to_debt_absence"
            name="obligationsConsent"
          />
        )}
        <DataProcessingInfo/>
        <ContinueButton disabled={submitting || (!obligationsConsent && refinancingOnly)} />
        {submitting && <LoaderModal />}
      </Box>
    </>
  )
}

export default LoanFields
